import { Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import { IconTile } from '../../../shared/components/IconTile';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { useAssistantRoles } from '../../../ai-assistant/hooks/useAssistantRoles';
import type { Document, DocumentCategory, DocumentType, DocumentTypeSection } from '../../../core/types/data-models';
import type { AssistantDocStats } from './AssistantDocumentCard';
import { AssistantRoleCard } from './AssistantRoleCard';
import { AssistantRoleRow } from './AssistantRoleRow';
import type { AssistantRoleData } from './assistantRoles';
import type { BlockedNextDoc } from './documentTypeCatalog';

export type AiAssistantsLayout = 'row' | 'column';

/**
 * Roster of role-based AI Assistants (docs/AI-Assistant-Roles.md §7.1),
 * ordered by role.order. Two layouts share the same header block:
 *  - column (one AssistantRoleCard per role, documents listed underneath — the original layout)
 *  - row (default — one AssistantRoleRow per role, documents laid out left-to-right as cards)
 */
export function AiAssistantsSection({
  docs,
  docTypeMap,
  inReviewCountByDoc,
  unresolvedCommentCountByDoc,
  entityCountByDoc,
  visualizationCountByDoc,
  presentationCountByDoc,
  recommendedItems,
  blockedByCategory,
  remainingByCategory,
  rootSectionsByDocType,
  layout,
  onLayoutChange,
  onOpenDocument,
  onGenerateRecommended,
}: {
  docs: Document[];
  docTypeMap: Map<string, DocumentType>;
  inReviewCountByDoc: Map<number, number>;
  unresolvedCommentCountByDoc: Map<number, number>;
  entityCountByDoc: Map<number, number>;
  visualizationCountByDoc: Map<number, number>;
  presentationCountByDoc: Map<number, number>;
  /** Not-yet-generated document types (Next Steps' list) — shown as extra cards per role in the row layout only. */
  recommendedItems: { docType: DocumentType; category: DocumentCategory | undefined }[];
  /** Per-category: which not-yet-generatable root doc type is closest to being unlocked, and what's still missing — keyed by DocumentCategory.type. */
  blockedByCategory: Map<string, BlockedNextDoc>;
  /** Per-category: count of active root document types not yet generated — keyed by DocumentCategory.type. */
  remainingByCategory: Map<string, number>;
  /** Each doc type's top-level sections (already capped to the first 5 by defaultOrder), keyed by DocumentType.type. */
  rootSectionsByDocType: Map<string, DocumentTypeSection[]>;
  layout: AiAssistantsLayout;
  onLayoutChange: (layout: AiAssistantsLayout) => void;
  onOpenDocument: (id: number) => void;
  onGenerateRecommended: (docType: DocumentType) => void;
}) {
  const { roles, isLoading } = useAssistantRoles();
  const { canGenerate } = usePermissions();

  if (isLoading || roles.length === 0) return null;

  const statsFor = (docId: number): AssistantDocStats => ({
    reviewCount: inReviewCountByDoc.get(docId) ?? 0,
    commentCount: unresolvedCommentCountByDoc.get(docId) ?? 0,
    entityCount: entityCountByDoc.get(docId) ?? 0,
    visualizationCount: visualizationCountByDoc.get(docId) ?? 0,
    presentationCount: presentationCountByDoc.get(docId) ?? 0,
  });

  const sortDocsByDocumentTypeOrder = (docsToSort: Document[]) => {
    const docTypeOrderByType = new Map<string, number>();
    for (const dt of docTypeMap.values()) {
      if (dt.order != null) {
        docTypeOrderByType.set(dt.type, dt.order);
      }
    }

    return [...docsToSort].sort((a, b) => {
      const aOrder = docTypeOrderByType.get(a.documentType) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = docTypeOrderByType.get(b.documentType) ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name);
    });
  };

  // Each role's slice of the project, computed once for whichever layout is showing.
  const roleData: AssistantRoleData[] = roles.map((role) => ({
    role,
    relatedDocs: sortDocsByDocumentTypeOrder(
      docs.filter((d) => docTypeMap.get(d.documentType)?.categoryType === role.documentCategoryType),
    ),
    recommended: recommendedItems.filter((item) => item.docType.categoryType === role.documentCategoryType),
    blocked: blockedByCategory.get(role.documentCategoryType),
    remaining: remainingByCategory.get(role.documentCategoryType) ?? 0,
  }));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <IconTile icon={<AccountTreeIcon fontSize="inherit" />} color="secondary" size="sm" />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
              AI Intelligence Assistants
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Specialized autonomous specialists working together to guide your project
            </Typography>
          </Box>
        </Stack>
        <ToggleButtonGroup
          value={layout}
          exclusive
          size="small"
          onChange={(_, val) => {
            if (val) onLayoutChange(val);
          }}
          sx={{
            backgroundColor: 'var(--color-bg)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 'var(--radius-md)',
            p: 0.4,
            gap: 0.4,
            '& .MuiToggleButton-root': { border: 'none', borderRadius: '8px !important', px: 1 },
            '& .Mui-selected': {
              backgroundColor: 'var(--color-primary) !important',
              color: '#fff !important',
            },
          }}
        >
          <Tooltip title="Row layout">
            <ToggleButton value="row" sx={{ px: 1 }}>
              <ViewStreamIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Column layout">
            <ToggleButton value="column" sx={{ px: 1 }}>
              <ViewColumnIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Stack>

      {layout === 'column' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
              xl: 'repeat(5, 1fr)',
            },
            gap: 2,
          }}
        >
          {roleData.map((data) => (
            <AssistantRoleCard
              key={data.role.key}
              {...data}
              inReviewCountByDoc={inReviewCountByDoc}
              canGenerate={canGenerate}
              onOpenDocument={onOpenDocument}
              onGenerateRecommended={onGenerateRecommended}
            />
          ))}
        </Box>
      ) : (
        <Stack spacing={2}>
          {roleData.map((data) => (
            <AssistantRoleRow
              key={data.role.key}
              {...data}
              statsFor={statsFor}
              rootSectionsByDocType={rootSectionsByDocType}
              canGenerate={canGenerate}
              onOpenDocument={onOpenDocument}
              onGenerateRecommended={onGenerateRecommended}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
